import { RefetchQueryDescriptor, useApolloClient } from "@apollo/client";
import OneSchemaImporter from "@oneschema/react";
import { ImportButton } from "components/ImportButton";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { gql } from "generated-graphql";
import { OneSchemaImportProcessingMethod } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useAlerts } from "../Alerts/AlertProvider";
import { ImportMenuItem } from "./ImportMenuItem";

export interface ImporterProps {
  templateType: OneSchemaTemplateType;
  refetchQueries?: RefetchQueryDescriptor[];
  label?: string;
  style?: React.CSSProperties;
  processingMethod?: OneSchemaImportProcessingMethod;
  postProcessingFunction?: (
    handleSuccessfulImport: (message: string) => Promise<void>,
    oneSchemaImportId: string
  ) => Promise<void>;
  mode?: "button" | "dropdown";
}

const INITIALIZE_IMPORT = gql(`
  mutation InitializeOneSchemaImport($tenantId: String, $templateType: String!, $processingMethod: OneSchemaImportProcessingMethod!) {
    initializeOneSchemaImport(tenantId: $tenantId, templateType: $templateType, processingMethod: $processingMethod) {
      oneSchemaImportId
      clientId
      sessionToken
    }
  }
`);

const PROCESS_ONESCHEMA_IMPORT = gql(`
  mutation ProcessOneSchemaImport($input: ProcessOneschemaFileUploadInput!) {
    processOneschemaFileUpload(input: $input)
  }
`);

type ImportSession = {
  oneSchemaImportId: string;
  clientId: string;
  sessionToken: string;
};

export function Importer({
  templateType,
  refetchQueries = [],
  label,
  style,
  processingMethod = OneSchemaImportProcessingMethod.Webhook,
  mode = "button",
}: ImporterProps) {
  const { tenantId } = useParams();
  const alerts = useAlerts();
  const apolloClient = useApolloClient();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [session, setSession] = useState<ImportSession | null>(null);

  const handleOpenImporter = async () => {
    setIsLoading(true);
    try {
      const { data } = await apolloClient.mutate({
        mutation: INITIALIZE_IMPORT,
        variables: { tenantId, templateType, processingMethod },
      });
      const init = data?.initializeOneSchemaImport;
      if (!init) throw new Error("Failed to initialize importer");

      setSession({
        oneSchemaImportId: init.oneSchemaImportId,
        clientId: init.clientId,
        sessionToken: init.sessionToken,
      });

      setIsOpen(true);
    } catch (error) {
      console.error("Error opening importer:", error);
      alerts.error("Failed to open importer. Please try again.");
    }
  };

  const handleCloseImporter = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSuccess = useCallback(async () => {
    if (!session?.oneSchemaImportId) {
      throw new Error("No active OneSchema import session");
    }

    const { data } = await apolloClient.mutate({
      mutation: PROCESS_ONESCHEMA_IMPORT,
      variables: {
        input: {
          tenantId: tenantId ?? "",
          oneSchemaImportId: session.oneSchemaImportId,
        },
      },
    });

    const finalizeResult = data?.processOneschemaFileUpload;
    if (!finalizeResult) {
      throw new Error("Failed to finalize OneSchema import");
    }

    alerts.success(`Successfully created job: ${finalizeResult}`);
    if (refetchQueries.length) {
      await apolloClient.refetchQueries({
        include: refetchQueries,
      });
    }
    setIsSuccess(true);
    handleCloseImporter();
  }, [
    alerts,
    apolloClient,
    handleCloseImporter,
    refetchQueries,
    session?.oneSchemaImportId,
    tenantId,
  ]);

  const onLaunched = () => {
    setIsLoading(false);
  };

  const onError = (message: any) => {
    setIsLoading(false);
    console.error(message);
    alerts.error("An error occurred during the import");
  };

  const importer = isOpen && session && (
    <OneSchemaImporter
      isOpen={isOpen}
      onRequestClose={handleCloseImporter}
      clientId={session.clientId}
      sessionToken={session.sessionToken}
      devMode={import.meta.env.MODE === "development"}
      className="oneschema-importer"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 1300,
      }}
      onLaunched={onLaunched}
      onSuccess={onSuccess}
      onError={onError}
      // Don't save sessions because we'll lose our tracking metadata
      saveSession={false}
    />
  );

  const disabled = isOpen || isLoading;

  return (
    <>
      {importer}
      {mode === "button" && (
        <ImportButton
          loading={isLoading}
          disabled={disabled}
          success={isSuccess}
          onClick={handleOpenImporter}
          label={label}
          style={{ ...style }}
        />
      )}
      {mode === "dropdown" && (
        <ImportMenuItem
          loading={isLoading}
          disabled={disabled}
          success={isSuccess}
          onClick={handleOpenImporter}
          label={label}
          style={{ ...style }}
        />
      )}
    </>
  );
}
